import {PayoutRange} from './types'

export const normalizePayoutStatus = (status?: string | null) => {
  const normalized = String(status || '')
    .trim()
    .toUpperCase()
  if (!normalized) return ''
  if (normalized === 'SUCCESS') return 'SUCCESS'
  if (normalized === 'SYNCED') return 'SYNCED'
  if (normalized === 'FAILED') return 'FAILED'
  if (normalized === 'ERROR') return 'ERROR'
  if (normalized === 'PENDING') return 'PENDING'
  return normalized
}

export const formatLabel = (value?: string | null) => {
  const raw = String(value || '').trim()
  if (!raw) return '-'
  return raw
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

export const formatDate = (value?: string | null, includeTime = false) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const numericMonth = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  if (!includeTime) return `${day}-${numericMonth}-${year}`

  const time = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  return `${time}, ${day}-${numericMonth}-${year}`
}

export const formatPayoutRange = (range?: PayoutRange | null) => {
  if (!range?.from && !range?.to) return '-'
  const from = formatDate(range?.from)
  const to = formatDate(range?.to)
  return `${from} - ${to}`
}

export const formatCurrency = (amount?: number | null, currency = 'USD') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '-'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount))
  } catch {
    return `$${Number(amount).toFixed(2)}`
  }
}

export const parseApiMessage = (message?: string | null) => {
  if (!message) return ''
  try {
    const parsed = JSON.parse(message)
    const error = parsed?.Fault?.Error?.[0]
    return error?.Detail || error?.Message || message
  } catch {
    return message
  }
}

export const getSettlementOrPayoutId = (row: any) => {
  return (
    row?.grouped_payout_id ||
    row?.settlement_id ||
    row?.payout_id ||
    (!row?.__synthetic_id ? row?.id : null) ||
    row?.estimated_payout_at ||
    '-'
  )
}

const sanitizeSyncDocument = (doc: string) =>
  String(doc)
    .trim()
    .replace(/^CHECK-/i, '')
    .replace(/^SETTLEMENT-/i, '')
    .replace(/^Settlement\s*-\s*/i, '')
    .replace(/^Settlement\s+/i, '')
    .trim()

export const getSyncDocumentNo = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((doc) => sanitizeSyncDocument(String(doc)))
      .filter(Boolean)
      .join(', ')
  }
  if (!value || value === 'None') return ''
  return sanitizeSyncDocument(String(value))
}

export const getChannelName = (row: any) =>
  row?.platform?.display_name ||
  row?.platform_info?.name ||
  row?.platform?.platform_info?.name ||
  row?.channel ||
  row?.channel_name ||
  row?.display_name ||
  ''
